// Supplier credits: debit notes drafted against one source bill, then
// marked sent and applied. Applying posts the AP adjustment journals
// (DR what-you-owe / CR the expense accounts, the exact reversal of bill
// posting) and flips the note to Applied — the status is the idempotency
// guard, so a second apply is rejected rather than double-posted.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { BillPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import { DocumentLineRow } from "@/features/document-lines/DocumentLineRow";
import {
  clearDocumentDraft,
  emptyDraftLine,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
  type DocumentDraft,
} from "@/features/document-lines/documentDraft";
import {
  toMinor,
  toSupplierCreditLinePayload,
} from "@/features/document-lines/productDetails";
import { useOrganization } from "@/lib/organization";
import {
  createSupplierCredit,
  listSupplierCredits,
  type SupplierCredit,
} from "@/lib/api";

const SUPPLIER_CREDIT_DRAFT_PREFIX =
  "niche-accounting.supplier-credit-draft.v1";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "applied":
      return "success";
    case "sent":
      return "info";
    case "void":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function SupplierCreditsPage({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";
  const sourceBill = useRouterState({
    select: (state) => {
      const bill = (state.location.search as Record<string, unknown>).bill;
      return typeof bill === "string" ? bill : "";
    },
  });

  const [notes, setNotes] = useState<SupplierCredit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Creation draft, mirrored per organization (see documentDraft). The mirror's
  // `crmContactId` slot carries the selected source bill id — supplier
  // credits aren't linked to a CRM contact, and the generic draft has no
  // dedicated source-document field.
  const [draft, setDraft] = useState<DocumentDraft>(newDocumentDraft);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Client-side table controls over the fetched list.
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listSupplierCredits()
      .then(setNotes)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Draft mirror: load from this browser session when the organization is known,
  // then save on every change. A failed storage read yields a fresh draft.
  useEffect(() => {
    setDraft(
      loadDocumentDraft(SUPPLIER_CREDIT_DRAFT_PREFIX, organizationId) ??
        newDocumentDraft(),
    );
    setLoadedDraftKey(organizationId);
  }, [organizationId]);
  useEffect(() => {
    if (loadedDraftKey !== organizationId) return;
    saveDocumentDraft(SUPPLIER_CREDIT_DRAFT_PREFIX, organizationId, draft);
  }, [draft, loadedDraftKey, organizationId]);
  useEffect(() => {
    if (!sourceBill || loadedDraftKey !== organizationId) return;
    setDraft((current) =>
      current.crmContactId === sourceBill
        ? current
        : { ...current, crmContactId: sourceBill },
    );
    setCreatorOpen(true);
  }, [loadedDraftKey, organizationId, sourceBill]);

  const updateDraft = (patch: Partial<DocumentDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const updateLine = (
    index: number,
    patch: Partial<DocumentDraft["lines"][number]>,
  ) => {
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
  };

  const previewMinor = draft.lines.reduce(
    (sum, line) => sum + (toMinor(line.amount) ?? 0),
    0,
  );

  const clearDraft = () => {
    setDraft(newDocumentDraft());
    clearDocumentDraft(SUPPLIER_CREDIT_DRAFT_PREFIX, organizationId);
  };

  const openCreator = () => {
    setCreateError(null);
    setCreatorOpen(true);
  };

  const statusCounts = (notes ?? []).reduce<Record<string, number>>(
    (counts, note) => {
      const key = note.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  // Supplier names are typed freely today (no supplier list endpoint), so the
  // datalist is a convenience hint derived from credits already loaded.
  const supplierNames = [
    ...new Set(
      (notes ?? [])
        .map((note) => note.supplier_name.trim())
        .filter((name) => name.length > 0),
    ),
  ].sort((a, b) => a.localeCompare(b));

  const visibleNotes = (notes ?? [])
    .filter((note) => !statusTab || note.status.toLowerCase() === statusTab)
    .filter((note) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return (
        note.supplier_credit_number.toLowerCase().includes(query) ||
        note.supplier_name.toLowerCase().includes(query)
      );
    });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setCreateError(null);
    try {
      const billId = draft.crmContactId;
      if (!billId) throw new Error("Pick the bill this credit relates to.");
      const name = draft.partyName.trim();
      if (!name) throw new Error("Supplier name is required.");
      if (!draft.issueDate) throw new Error("Issue date is required.");
      await createSupplierCredit({
        bill_id: billId,
        supplier_name: name,
        issue_date: draft.issueDate,
        lines: toSupplierCreditLinePayload(draft.lines),
      });
      clearDraft();
      setCreatorOpen(false);
      reload();
    } catch (cause) {
      setCreateError(toErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={embedded ? "grid gap-4" : "grid gap-6"}>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" onClick={openCreator}>
            New supplier credit
          </Button>
        </div>
      ) : (
        <PageHeader
          title="Supplier credits"
          description="Credit against supplier bills — draft against a bill, mark sent, then apply to reduce what the bill owes."
          actions={
            <Button type="button" onClick={openCreator}>
              New supplier credit
            </Button>
          }
        />
      )}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {error && notes === null ? null : (
        <div className="grid gap-3">
            <div className="surface grid gap-3 rounded-2xl p-4">
              <div className="flex flex-wrap items-end gap-3">
                <label
                  htmlFor="supplier-credits-search"
                  className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
                >
                  Search
                  <Input
                    id="supplier-credits-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Number or supplier…"
                    disabled={loading}
                  />
                </label>
                <label
                  htmlFor="supplier-credits-status"
                  className="grid min-w-40 gap-1 text-xs font-medium"
                >
                  Status
                  <select
                    id="supplier-credits-status"
                    className="select"
                    value={statusTab}
                    onChange={(e) => setStatusTab(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">
                      All statuses ({(notes ?? []).length})
                    </option>
                    <option value="draft">
                      Draft ({statusCounts.draft ?? 0})
                    </option>
                    <option value="sent">
                      Sent ({statusCounts.sent ?? 0})
                    </option>
                    <option value="applied">
                      Applied ({statusCounts.applied ?? 0})
                    </option>
                    <option value="void">
                      Void ({statusCounts.void ?? 0})
                    </option>
                  </select>
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={reload}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Refresh"}
                </Button>
              </div>
            </div>

            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="supplier-credits-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && notes === null ? (
                      <TableRow>
                        <TableCell colSpan={5}>Loading…</TableCell>
                      </TableRow>
                    ) : (notes ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No supplier credits yet — create the first one above.
                        </TableCell>
                      </TableRow>
                    ) : visibleNotes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No supplier credits match these filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleNotes.map((note) => (
                        <TableRow key={note.id}>
                          <TableCell className="font-mono text-xs">
                            <Link
                              to="/supplier-credits/$supplierCreditId"
                              params={{ supplierCreditId: note.id }}
                              className="hover:underline"
                            >
                              {note.supplier_credit_number}
                            </Link>
                          </TableCell>
                          <TableCell>{note.supplier_name}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {new Date(note.issue_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <StatusDot
                              tone={statusTone(note.status)}
                              label={plainStatus(note.status)}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: note.total_minor,
                              currency: "USD",
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="supplier-credits-stack"
            >
              {loading && notes === null ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Loading…
                </p>
              ) : visibleNotes.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  {(notes ?? []).length === 0
                    ? "No supplier credits yet — create the first one above."
                    : "No supplier credits match these filters."}
                </p>
              ) : (
                visibleNotes.map((note) => (
                  <article
                    key={note.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to="/supplier-credits/$supplierCreditId"
                        params={{ supplierCreditId: note.id }}
                        className="record-title underline-offset-4 hover:underline"
                      >
                        {note.supplier_credit_number}
                      </Link>
                      <span className="tabular-nums">
                        {formatMoney({
                          cents: note.total_minor,
                          currency: "USD",
                        })}
                      </span>
                    </div>
                    <CardField label="Supplier">{note.supplier_name}</CardField>
                    <CardField label="Issued">
                      {new Date(note.issue_date).toLocaleDateString()}
                    </CardField>
                    <CardField label="Status">
                      <StatusDot
                        tone={statusTone(note.status)}
                        label={plainStatus(note.status)}
                      />
                    </CardField>
                    <div className="flex flex-wrap gap-2 pt-3">
                      <Button asChild variant="secondary" size="sm">
                        <Link
                          to="/supplier-credits/$supplierCreditId"
                          params={{ supplierCreditId: note.id }}
                        >
                          Open
                        </Link>
                      </Button>
                    </div>
                  </article>
                ))
              )}
            </div>
        </div>
      )}

      <Drawer
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        title="New supplier credit"
        description="Drafted against one bill — nothing posts until the credit is marked sent and applied. Closing this panel keeps your draft in this browser session. The bill must belong to the same books."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreatorOpen(false)}
            >
              Back to supplier credits
            </Button>
            {draft.partyName ||
            draft.crmContactId ||
            draft.lines.some((line) => line.description || line.amount) ? (
              <Button type="button" variant="ghost" onClick={clearDraft}>
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="supplier-credit-create-form"
              disabled={submitting}
              className="ms-auto"
            >
              {submitting ? "Drafting…" : "Create supplier credit"}
            </Button>
          </>
        }
      >
        <form
          id="supplier-credit-create-form"
          onSubmit={submit}
          className="grid gap-5"
          aria-label="Create supplier credit"
        >
          {createError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {createError}
            </div>
          ) : null}
          <label htmlFor="sc-bill" className="grid gap-1 text-xs font-medium">
            Bill
            <BillPicker
              id="sc-bill"
              value={draft.crmContactId}
              onChange={(billId) => updateDraft({ crmContactId: billId })}
              disabled={submitting}
              required
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label
              htmlFor="sc-supplier"
              className="grid gap-1 text-xs font-medium"
            >
              Supplier name
              <Input
                id="sc-supplier"
                value={draft.partyName}
                onChange={(e) => updateDraft({ partyName: e.target.value })}
                required
                placeholder="Acme Supplies"
                disabled={submitting}
                list={
                  supplierNames.length > 0
                    ? "supplier-credit-suppliers"
                    : undefined
                }
              />
              {supplierNames.length > 0 ? (
                <datalist id="supplier-credit-suppliers">
                  {supplierNames.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              ) : null}
            </label>
            <label htmlFor="sc-date" className="grid gap-1 text-xs font-medium">
              Issue date
              <Input
                id="sc-date"
                type="date"
                value={draft.issueDate}
                onChange={(e) => updateDraft({ issueDate: e.target.value })}
                required
                disabled={submitting}
              />
            </label>
          </div>
          <fieldset className="grid gap-3">
            <legend className="text-xs font-medium">Lines</legend>
            {draft.lines.map((line, index) => (
              <DocumentLineRow
                key={line.uid}
                idPrefix={`sc-line-${index}`}
                index={index}
                line={line}
                onChange={(patch) => updateLine(index, patch)}
                onRemove={() =>
                  setDraft((prev) => ({
                    ...prev,
                    lines:
                      prev.lines.length <= 1
                        ? prev.lines
                        : prev.lines.filter((_, i) => i !== index),
                  }))
                }
                canRemove={draft.lines.length > 1}
                showTax={false}
                accountLabel="Expense account"
                disabled={submitting}
              />
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    lines: [...prev.lines, emptyDraftLine()],
                  }))
                }
                disabled={submitting}
              >
                Add line
              </Button>
            </div>
          </fieldset>
          <p
            className="text-sm text-[var(--muted-foreground)]"
            aria-live="polite"
          >
            Total:{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: previewMinor, currency: "USD" })}
            </span>
          </p>
        </form>
      </Drawer>
    </div>
  );
}
